/**
 * AppProvider - Root Provider Composition
 *
 * This component serves as a centralized wrapper that composes all of the
 * application's context providers into a single component. It is rendered
 * at the root layout level (`app/layout.tsx`) and provides global state
 * and functionality to the entire application.
 *
 * All data is fetched client-side by individual providers via SWR hooks,
 * eliminating server-side data fetching from the root layout and preventing
 * RSC prefetch amplification.
 *
 * ## Provider Hierarchy (outermost to innermost)
 *
 * 1. **SettingsProvider** - Application settings and feature flags
 * 2. **UserProvider** - Current user authentication and profile
 * 3. **AppBackgroundProvider** - App background image/URL based on user preferences
 * 4. **ProviderContextProvider** - LLM provider configuration
 * 5. **ModalProvider** - Global modal state management
 * 6. **SidebarStateProvider** - Sidebar open/closed state
 * 7. **QueryControllerProvider** - Search/Chat mode + query lifecycle (rendered inside AppLayout, after Router context is available)
 */

import { UserProvider } from "@/app/providers/UserProvider";
import { ProviderContextProvider } from "@/features/chat/components/ProviderContext";
import { SettingsProvider } from "@/app/providers/SettingsProvider";
import { ModalProvider } from "@/features/context/components/ModalContext";
import { StateProvider as SidebarStateProvider } from "@/shared/ui/layouts/sidebar-layouts";
import { AppBackgroundProvider } from "@/app/providers/AppBackgroundProvider";
import ToastProvider from "@/app/providers/ToastProvider";

interface AppProviderProps {
  children: React.ReactNode;
}

export default function AppProvider({ children }: AppProviderProps) {
  return (
    <SettingsProvider>
      <UserProvider>
        <AppBackgroundProvider>
          <ProviderContextProvider>
            <ModalProvider>
              <SidebarStateProvider>
                <ToastProvider>{children}</ToastProvider>
              </SidebarStateProvider>
            </ModalProvider>
          </ProviderContextProvider>
        </AppBackgroundProvider>
      </UserProvider>
    </SettingsProvider>
  );
}
